import React from 'react';
import { User, Calendar, BookOpen, Activity, Mail, Phone, Heart } from 'lucide-react';
import { cn } from '../../lib/utils';

export default function AttendanceAnalytics({ student, overall, monthly, subjects }) {
    if (!student || !overall) return null;

    return (
        <div className="space-y-6 w-full overflow-hidden">
            {/* Header Profile Card */}
            <div className="bg-card border border-border rounded-2xl p-4 md:p-8 flex flex-col md:flex-row items-center md:items-start gap-6 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full blur-3xl -mr-16 -mt-16"></div>

                <div className="h-24 w-24 md:h-32 md:w-32 bg-primary rounded-[2rem] flex items-center justify-center text-primary-foreground shadow-xl shadow-primary/20 rotate-3 flex-shrink-0 animate-in zoom-in duration-500">
                    <span className="text-4xl md:text-5xl font-black font-heading">{student.name[0]}</span>
                </div>

                <div className="flex-1 text-center md:text-left space-y-4">
                    <div>
                        <h1 className="text-2xl md:text-4xl font-black font-heading text-foreground tracking-tight">{student.name}</h1>
                        <div className="flex flex-wrap items-center justify-center md:justify-start gap-2 mt-2">
                            <span className="px-3 py-1 bg-primary/10 text-primary rounded-lg text-xs font-bold uppercase tracking-wider">
                                Year {student.year}
                            </span>
                            <span className="px-3 py-1 bg-secondary text-secondary-foreground rounded-lg text-xs font-bold uppercase tracking-wider">
                                {student.branch}
                            </span>
                            <span className="px-3 py-1 bg-accent/10 text-accent-foreground rounded-lg text-xs font-bold uppercase tracking-wider">
                                {student.sectionName}
                            </span>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                        <div className="flex items-center gap-3 p-3 bg-secondary/30 rounded-xl border border-border/50">
                            <div className="h-8 w-8 rounded-lg bg-background flex items-center justify-center text-muted-foreground">
                                <Activity size={16} />
                            </div>
                            <div className="flex flex-col text-left">
                                <span className="text-[10px] uppercase font-bold text-muted-foreground tracking-widest">Roll Number</span>
                                <span className="text-sm font-mono font-bold leading-none">{student.rollNumber}</span>
                            </div>
                        </div>
                        <div className="flex items-center gap-3 p-3 bg-secondary/30 rounded-xl border border-border/50">
                            <div className="h-8 w-8 rounded-lg bg-background flex items-center justify-center text-muted-foreground">
                                <Mail size={16} />
                            </div>
                            <div className="flex flex-col text-left overflow-hidden">
                                <span className="text-[10px] uppercase font-bold text-muted-foreground tracking-widest">Email</span>
                                <span className="text-sm font-medium truncate leading-none">{student.email}</span>
                            </div>
                        </div>
                    </div>

                    <div className="pt-2 flex flex-wrap justify-center md:justify-start gap-6 text-sm">
                        <div className="flex items-center gap-2 text-muted-foreground">
                            <div className="h-2 w-2 rounded-full bg-emerald-500"></div>
                            <span className="font-semibold">Active Enrollment</span>
                        </div>
                        <div className="flex items-center gap-2 text-muted-foreground">
                            <Phone size={14} className="text-primary" />
                            <span>{student.phone}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-card border border-border p-6 rounded-2xl shadow-sm relative overflow-hidden group">
                    <div className="absolute -right-4 -top-4 opacity-5 group-hover:scale-110 group-hover:rotate-12 transition-all duration-500">
                        <Activity size={120} />
                    </div>
                    <h3 className="text-xs font-bold text-muted-foreground mb-4 uppercase tracking-[0.2em]">Overall Attendance</h3>
                    <div className="flex items-end gap-3">
                        <span className={cn(
                            "text-5xl font-black font-heading leading-none",
                            overall.percentage >= 75 ? 'text-emerald-500' : 'text-rose-500'
                        )}>
                            {overall.percentage}%
                        </span>
                        <span className="text-muted-foreground font-medium mb-1">
                            ({overall.present}/{overall.total} Classes)
                        </span>
                    </div>
                    <div className="mt-6 h-3 w-full bg-secondary rounded-full overflow-hidden p-0.5">
                        <div
                            className={cn(
                                "h-full rounded-full transition-all duration-1000 ease-out shadow-sm",
                                overall.percentage >= 75 ? 'bg-emerald-500' : 'bg-rose-500'
                            )}
                            style={{ width: `${overall.percentage}%` }}
                        ></div>
                    </div>
                </div>

                <div className="bg-card border border-border p-6 rounded-2xl shadow-sm relative overflow-hidden group">
                    <div className="absolute -right-4 -top-4 opacity-5 group-hover:scale-110 group-hover:-rotate-12 transition-all duration-500">
                        <Calendar size={120} />
                    </div>
                    <h3 className="text-xs font-bold text-muted-foreground mb-4 uppercase tracking-[0.2em]">Current Month ({monthly.month})</h3>
                    <div className="flex items-end gap-3">
                        <span className="text-5xl font-black font-heading leading-none text-blue-500">
                            {monthly.percentage}%
                        </span>
                        <span className="text-muted-foreground font-medium mb-1">
                            ({monthly.present}/{monthly.total} Sessions)
                        </span>
                    </div>
                    <div className="mt-6 h-3 w-full bg-secondary rounded-full overflow-hidden p-0.5">
                        <div
                            className="h-full bg-blue-500 rounded-full transition-all duration-1000 ease-out shadow-sm"
                            style={{ width: `${monthly.percentage}%` }}
                        ></div>
                    </div>
                </div>
            </div>

            {/* Family Details Card */}
            <div className="bg-card border border-border rounded-2xl p-6 shadow-sm overflow-hidden relative">
                <div className="flex items-center gap-2 mb-6 text-muted-foreground">
                    <Heart size={18} className="text-rose-500" />
                    <h3 className="font-bold text-lg text-foreground">Family & Contact</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div className="space-y-1">
                        <span className="text-[10px] uppercase font-black text-muted-foreground/60 tracking-widest">Father's Name</span>
                        <p className="font-bold text-foreground">{student.fatherName}</p>
                    </div>
                    <div className="space-y-1">
                        <span className="text-[10px] uppercase font-black text-muted-foreground/60 tracking-widest">Primary Contact</span>
                        <p className="font-bold text-foreground flex items-center gap-2">
                            {student.fatherPhone}
                        </p>
                    </div>
                    <div className="space-y-1">
                        <span className="text-[10px] uppercase font-black text-muted-foreground/60 tracking-widest">Emergency Email</span>
                        <p className="font-bold text-foreground truncate">{student.fatherEmail}</p>
                    </div>
                </div>
            </div>

            {/* Subject Breakdown */}
            <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-sm">
                <div className="p-6 border-b border-border bg-secondary/20 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="h-10 w-10 bg-background rounded-xl flex items-center justify-center text-primary border border-border">
                            <BookOpen size={20} />
                        </div>
                        <h3 className="font-black text-xl font-heading">Academic Analytics</h3>
                    </div>
                </div>

                <div className="divide-y divide-border">
                    {subjects.length === 0 ? (
                        <div className="p-16 text-center">
                            <div className="inline-flex h-20 w-20 items-center justify-center rounded-full bg-muted/30 text-muted-foreground mb-4">
                                <BookOpen size={40} />
                            </div>
                            <p className="text-muted-foreground font-medium">No academic records found for this period.</p>
                        </div>
                    ) : (
                        subjects.map((subj, idx) => {
                            const pct = subj.total > 0 ? Math.round((subj.present / subj.total) * 100) : 0;
                            return (
                                <div key={idx} className="p-4 md:p-6 flex flex-col md:flex-row md:items-center justify-between gap-6 hover:bg-muted/5 transition-all group">
                                    <div className="flex items-center gap-4">
                                        <div className="h-12 w-12 bg-secondary rounded-2xl flex items-center justify-center font-black text-sm text-foreground group-hover:scale-110 group-hover:rotate-3 transition-transform">
                                            {subj.code}
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-foreground text-lg">{subj.name}</h4>
                                            <p className="text-sm text-muted-foreground font-medium">Attendance: {subj.present} of {subj.total} labs/classes</p>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-6 md:min-w-[280px]">
                                        <div className="flex-1 space-y-2">
                                            <div className="flex items-center justify-between text-xs font-bold uppercase tracking-tighter">
                                                <span className={pct >= 75 ? 'text-emerald-500' : 'text-orange-500'}>Compliance</span>
                                                <span className="text-foreground">{pct}%</span>
                                            </div>
                                            <div className="h-2 bg-secondary rounded-full overflow-hidden">
                                                <div
                                                    className={cn(
                                                        "h-full rounded-full transition-all duration-700",
                                                        pct >= 75 ? 'bg-emerald-500' : 'bg-orange-500'
                                                    )}
                                                    style={{ width: `${pct}%` }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
}
